import React from 'react';
import { Button } from '../../components/ui/button';
import { Upload, Image as ImageIcon } from 'lucide-react';

const ImageControlsPanel = ({
  imageUrl,
  overlayOpacity = 30,
  imageBrightness = 100,
  onImageUrlChange,
  onOverlayChange,
  onBrightnessChange,
  onRemove,
  onUploadClick,
  onSuggestionsClick,
  onLibraryClick,
  uploading = false,
  uploadInputId = 'img-upload',
  onFileUpload,
  label = 'Background Image',
  helpText = 'URL da imagem de fundo. Faça upload ou escolha das sugestões.'
}) => {
  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-[#2B2D42]">{label}</label>

      {/* Image Preview */}
      {imageUrl && (
        <div className="relative">
          <img src={imageUrl} alt="Preview" className="w-full h-48 object-cover rounded-lg border border-gray-300" />
          <Button type="button" variant="destructive" size="sm" onClick={onRemove} className="absolute top-2 right-2">
            Remover
          </Button>
        </div>
      )}

      {/* URL Input */}
      <input
        type="text"
        value={imageUrl || ''}
        onChange={(e) => onImageUrlChange(e.target.value)}
        placeholder="https://..."
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
      />

      {/* Upload Options */}
      <div className="grid grid-cols-3 gap-2">
        <div>
          {onFileUpload && (
            <input type="file" id={uploadInputId} accept="image/*" onChange={onFileUpload} className="hidden" disabled={uploading} />
          )}
          <Button type="button" variant="outline" size="sm" className="w-full"
            onClick={() => onFileUpload ? document.getElementById(uploadInputId).click() : (onUploadClick && onUploadClick())}
            disabled={uploading}>
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'A enviar...' : 'Upload'}
          </Button>
        </div>
        <Button type="button" variant="outline" size="sm" className="w-full"
          onClick={onLibraryClick} disabled={!onLibraryClick}>
          <ImageIcon className="w-4 h-4 mr-2" /> Biblioteca
        </Button>
        <Button type="button" variant="outline" size="sm"
          className="w-full border-[#00B4D8] text-[#00B4D8] hover:bg-[#E0F2FE]"
          onClick={onSuggestionsClick} disabled={!onSuggestionsClick}>
          <ImageIcon className="w-4 h-4 mr-2" /> Sugestões
        </Button>
      </div>
      <p className="text-xs text-[#6C757D]">{helpText}</p>

      {/* Overlay & Brightness Controls */}
      {imageUrl && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-sm font-bold text-[#2B2D42]">Image Controls</h4>

          {/* Live Preview */}
          <div className="relative h-28 rounded-lg overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${imageUrl})`, filter: `brightness(${(imageBrightness || 100) / 100})` }} />
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${(overlayOpacity || 30) / 100})` }} />
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <span className="text-white font-bold text-lg drop-shadow-lg">Preview</span>
            </div>
          </div>

          {/* Overlay */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-[#2B2D42]">Overlay Darkness: {overlayOpacity}%</label>
              <span className="text-xs text-[#6C757D]">0% = no overlay, 80% = full dark</span>
            </div>
            <input type="range" min="0" max="80" step="5" value={overlayOpacity}
              onChange={(e) => onOverlayChange(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#00B4D8]" />
            <div className="flex justify-between text-xs text-[#6C757D] mt-1">
              <span>Full Photo</span><span>Darker</span>
            </div>
          </div>

          {/* Brightness */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-[#2B2D42]">Image Brightness: {imageBrightness}%</label>
              <span className="text-xs text-[#6C757D]">50% = dark, 100% = normal, 150% = bright</span>
            </div>
            <input type="range" min="50" max="150" step="5" value={imageBrightness}
              onChange={(e) => onBrightnessChange(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#FF6B35]" />
            <div className="flex justify-between text-xs text-[#6C757D] mt-1">
              <span>Darker</span><span>Normal</span><span>Brighter</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageControlsPanel;
